import React, { useEffect, ComponentType } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';

// images
import previewImg from 'assets/pics/ip-preview-premium.jpg';

// Actions
import {
  pullOvpDetails as pullOvpDetailsAction,
  createItem as createItemAction,
  loadJWPlayers as loadJWPlayersAction,
  PullOvpParams,
  CreateItem,
} from 'ducks/merchantDucks/items/actions';
import { loadExternalAccount as loadExternalAccountAction } from 'ducks/merchantDucks/externalAccount/actions';

// Utils
import capitalize from 'lodash/capitalize';
import { qbrickThumbnailImage } from 'utils/getQbrickThumbnail';

// types
import RootState from 'ducks/RootState';
import ExternalAccountState from 'ducks/merchantDucks/externalAccount/types';
import { QuickAddData } from 'pages/MerchantPages/Assets/assetTypes';
import ItemsState, { ItemDetails } from 'ducks/merchantDucks/items/types';

// Components
import Loader from 'components/Loader';
import { BlueTypography } from 'components/SharedStyledElements';
import BrightcoveQuickAddForm from './forms/BrightcoveQuickAdd';
import JwPlayerQuickAddForm from './forms/JwPlayerQuickAdd';
import LivestreamQuickAddForm from './forms/LivestreamQuickAdd';
import OoyalaQuickAddForm from './forms/OoyalaQuickAdd';
import PikselQuickAddForm from './forms/PikselQuickAdd';
import QbrickQuickAddForm from './forms/QbrickQuickAdd';

interface QuickAdd {
  [key: string]: ComponentType<any>;
}

const ovpQuickAddComponents: QuickAdd = {
  brightcove: BrightcoveQuickAddForm,
  jwplayer: JwPlayerQuickAddForm,
  livestream: LivestreamQuickAddForm,
  ooyala: OoyalaQuickAddForm,
  piksel: PikselQuickAddForm,
  qbrick: QbrickQuickAddForm,
};

interface StateProps {
  externalAccount: ExternalAccountState;
  assets: ItemsState;
  areDetailsPulled: boolean;
  assetDetails: ItemDetails;
}

interface OwnProps {
  ovpType: string;
  accessControlTypeId: number;
  onBackButtonClick: () => void;
}

interface DispatchProps {
  loadExternalAccount: (type: string) => any;
  loadJWPlayers: () => any;
  pullOvpDetails: (params: PullOvpParams) => any;
  createItem: (asset: CreateItem) => any;
}

type Props = StateProps & DispatchProps & OwnProps;

const QuickAdd = (props: Props) => {
  const {
    ovpType,
    externalAccount: { keys, isFetching },
    createItem,
    assets: {
      jwPlayers: { players },
    },
    accessControlTypeId,
    areDetailsPulled,
    assetDetails: { isCreated, isSaving },
    onBackButtonClick,
  } = props;

  useEffect(() => {
    const { loadExternalAccount, loadJWPlayers } = props;

    loadExternalAccount(ovpType);

    if (ovpType === 'jwplayer') loadJWPlayers();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (areDetailsPulled && !isCreated && !isSaving) createOvpAsset();
  }, [areDetailsPulled, isCreated]);

  const OvpComponent = ovpQuickAddComponents[ovpType];
  const text = `${capitalize(ovpType)} Details:`;

  const createBrightcoveAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: { name, images, description, account_id, id },
      },
    } = props;

    const { poster = { src: '' }, thumbnail = { src: '' } } = images || {};
    const itemType = `${ovpType}_asset`;
    const title = name!;
    const metadata = {
      preview_title: name,
      preview_description: description ? description.replace(/\n/g, '<br />') : '',
      paywall_cover_photo: poster.src || thumbnail.src || previewImg,
      thumbnail_image: poster.src || thumbnail.src || previewImg,
    };
    const content = JSON.stringify({ account_id, video_id: id });
    const externalAssetId = id;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createJwPlayerAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: { title: propsTitle, thumbnail_url, description, key, player_name, player_id },
      },
    } = props;

    const itemType = 'jw_asset';
    const title = propsTitle!;
    const metadata = {
      preview_title: title,
      preview_description: description ? description.replace(/\n/g, '<br />') : '',
      paywall_cover_photo: thumbnail_url,
      thumbnail_image: thumbnail_url,
    };
    const content = JSON.stringify({ video_id: key, player_id, player_name });
    const externalAssetId = key;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createLivestreamAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: { caption, thumbnailUrl, thumbnailUrlSmall, eventId, description, account_id, id },
      },
    } = props;

    const itemType = ovpType === 'jwplayer' ? 'jw_asset' : `${ovpType}_asset`;
    const title = caption!;
    const metadata = {
      preview_title: caption,
      preview_description: description ? description.replace(/\n/g, '<br />') : '',
      paywall_cover_photo: thumbnailUrl,
      thumbnail_image: thumbnailUrlSmall,
    };
    const content = JSON.stringify({ account_id, event_id: eventId, video_id: id });
    const externalAssetId = id;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createOoyalaAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: { name, description, preview_image_url, embed_code, player_id, asset_type },
      },
    } = props;

    const itemType = `${ovpType}_asset`;
    const title = name!;
    const metadata = {
      preview_title: name,
      preview_description: description ? description.replace(/\n/g, '<br />') : '',
      paywall_cover_photo: preview_image_url,
      thumbnail_image: preview_image_url,
    };
    const content = JSON.stringify({
      embed_code,
      name,
      player_id,
      type: asset_type,
      preview_image: preview_image_url,
    });
    const externalAssetId = embed_code;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createPikselAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: { Title, Description, posterUrl, thumbnailUrl, uuid },
      },
    } = props;

    const itemType = `${ovpType}_asset`;
    const title = Title!;
    const metadata = {
      preview_title: Title,
      preview_description: Description ? Description.replace(/\n/g, '<br />') : '',
      paywall_cover_photo: posterUrl,
      thumbnail_image: thumbnailUrl,
    };
    const content = uuid;
    const externalAssetId = uuid;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createQbrickAssetPayload = () => {
    const {
      assets: {
        ovpVideoDetails: {
          id,
          metadata: { title, description },
          asset: { resources },
          thumbnails,
          tags,
          created,
          catalog,
        },
      },
    } = props;

    const itemType = `${ovpType}_asset`;
    const thumbnailImage = qbrickThumbnailImage(resources, thumbnails) || previewImg;
    const metadata: any = {
      preview_title: title,
      preview_description: description.replace(/\n/g, '<br />') || 'Asset description',
      paywall_cover_photo: thumbnailImage,
      thumbnail_image: thumbnailImage,
      preview_button_label: 'Buy',
    };

    if (created) {
      metadata.asset_created_at = created;
    }

    if (tags.length) {
      tags.forEach((x: string) => {
        metadata[`asset_${x}`] = 1;
      });
    }

    if (catalog && catalog.id) {
      metadata.asset_category = catalog.id;
    }

    const { account_id, client_id } = keys[ovpType].keys;
    const content = JSON.stringify({ account_id, client_id, media_id: id });
    const externalAssetId = id;

    return { itemType, content, title, metadata, externalAssetId, isQuickAdd: true };
  };

  const createOvpAsset = () => {
    let createAssetPayload: any;
    switch (ovpType) {
      case 'brightcove':
        createAssetPayload = createBrightcoveAssetPayload();
        break;
      case 'jwplayer':
        createAssetPayload = createJwPlayerAssetPayload();
        break;
      case 'livestream':
        createAssetPayload = createLivestreamAssetPayload();
        break;
      case 'ooyala':
        createAssetPayload = createOoyalaAssetPayload();
        break;
      case 'piksel':
        createAssetPayload = createPikselAssetPayload();
        break;
      case 'qbrick':
        createAssetPayload = createQbrickAssetPayload();
        break;
      default:
        break;
    }

    createAssetPayload.accessControlTypeId = accessControlTypeId;
    createAssetPayload.metadata.preview_button_label = 'Buy';
    createItem(createAssetPayload);
  };

  const handleSubmit = async (data: QuickAddData) => {
    const { pullOvpDetails } = props;
    const { eventId = '', player = '', videoId = 0 } = data || {};
    // These are only present for jwplayer
    const [playerId = '', playerName = ''] = player.split('.');

    await pullOvpDetails({ ovpType, eventId, videoId, playerId, playerName });
  };

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      <BlueTypography variant="h4">{text}</BlueTypography>
      <OvpComponent
        onSubmit={handleSubmit}
        keys={keys[ovpType] || {}}
        jwPlayers={players}
        onBackButtonClick={onBackButtonClick}
      />
    </>
  );
};

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  externalAccount: state.externalAccount,
  assets: state.items,
  areDetailsPulled: state.items.ovpVideoDetails.isPulled,
  assetDetails: state.items.itemDetails,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  loadExternalAccount: loadExternalAccountAction,
  loadJWPlayers: loadJWPlayersAction,
  pullOvpDetails: pullOvpDetailsAction,
  createItem: createItemAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickAdd);
